import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Search, X, Sprout, Copy, ArrowUp, ArrowDown } from 'lucide-react';

import useAdminProducts from '../../hooks/useAdminProducts';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Spinner from '../../components/ui/Spinner';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { useToast } from '../../components/ui/Toast';
import { formatPrice } from '../../utils/money';
import { primaryPhoto } from '../../utils/photos';
import { cloudinaryThumb, cloudinaryThumbSrcSet } from '../../utils/cloudinary';
import { parseApiError } from '../../utils/apiError';

export default function AdminProducts() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { confirm, dialog } = useConfirm();
  const [query, setQuery] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState(null); // null | 'title' | 'category' | 'price' | 'status'
  const [sortDir, setSortDir] = useState('asc');
  const [brokenPhotoIds, setBrokenPhotoIds] = useState(() => new Set());

  const markPhotoBroken = (id) =>
    setBrokenPhotoIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));

  const sort = sortField ? `${sortField}_${sortDir}` : 'newest';
  const { items, total, pages, loading, update, remove } = useAdminProducts({ page, q, sort, limit: 20 });

  const onSort = (field) => {
    if (sortField !== field) {
      setSortField(field);
      setSortDir('asc');
    } else if (sortDir === 'asc') {
      setSortDir('desc');
    } else {
      // third click on the same column clears the sort (back to server default order)
      setSortField(null);
      setSortDir('asc');
    }
    setPage(1);
  };

  const sortIcon = (field) =>
    sortField === field ? (
      sortDir === 'asc' ? <ArrowUp size={14} aria-hidden="true" /> : <ArrowDown size={14} aria-hidden="true" />
    ) : null;

  const onSearch = (e) => {
    e.preventDefault();
    setQ(query.trim());
    setPage(1);
  };

  const onClearSearch = () => {
    setQuery('');
    setQ('');
    setPage(1);
  };

  const onDelete = async (p) => {
    const ok = await confirm({
      title: 'Delete product',
      message: `Delete "${p.title}"? It will be hidden from the storefront.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await remove(p._id);
      addToast('Product deleted', { variant: 'success' });
    } catch (err) {
      addToast(parseApiError(err).formError || 'Could not delete product', { variant: 'error' });
    }
  };

  const toggleActive = async (p) => {
    try {
      await update(p._id, { active: !p.active });
    } catch (err) {
      addToast(parseApiError(err).formError || 'Could not update product', { variant: 'error' });
    }
  };

  const copyLink = async (p) => {
    const url = `${window.location.origin}/products/${p._id}`;
    try {
      await navigator.clipboard.writeText(url);
      addToast('Link copied!', { variant: 'success' });
    } catch {
      addToast('Could not copy link', { variant: 'error' });
    }
  };

  return (
    <div>
      <div className="ss-admin__header">
        <h1>Products</h1>
        <Button size="sm" onClick={() => navigate('/admin/products/new')}>
          <Plus size={16} aria-hidden="true" /> New product
        </Button>
      </div>

      <form className="ss-admin-search" onSubmit={onSearch} style={{ marginTop: 'var(--ss-space-4)' }}>
        <div className="ss-admin-search__field">
          <input
            className="ss-input"
            type="search"
            placeholder="Search products…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search products"
          />
          {query && (
            <button
              type="button"
              className="ss-admin-search__clear"
              onClick={onClearSearch}
              aria-label="Clear search"
            >
              <X size={16} aria-hidden="true" />
            </button>
          )}
        </div>
        <Button type="submit" variant="ghost" size="sm"><Search size={16} aria-hidden="true" /> Search</Button>
      </form>

      {loading ? (
        <div className="ss-state"><Spinner label="Loading products" size={32} /></div>
      ) : items.length === 0 ? (
        <div className="ss-state"><h2>{q ? 'No products match your search' : 'No products yet'}</h2></div>
      ) : (
        <>
          <Card className="ss-admin-table-wrap" style={{ marginTop: 'var(--ss-space-5)', padding: 0 }}>
            <div className="ss-table-scroll">
            <table className="ss-admin-table">
              <thead>
                <tr>
                  <th aria-label="Photo" />
                  <th>
                    <button type="button" className="ss-admin-table__sort" onClick={() => onSort('title')}>
                      Title {sortIcon('title')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="ss-admin-table__sort" onClick={() => onSort('category')}>
                      Category {sortIcon('category')}
                    </button>
                  </th>
                  <th style={{ textAlign: 'right' }}>
                    <button type="button" className="ss-admin-table__sort" onClick={() => onSort('price')}>
                      Price {sortIcon('price')}
                    </button>
                  </th>
                  <th style={{ textAlign: 'center' }}>
                    <button type="button" className="ss-admin-table__sort" onClick={() => onSort('status')}>
                      Status {sortIcon('status')}
                    </button>
                  </th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {items.map((p) => {
                  const photo = primaryPhoto(p.photos);
                  return (
                    <tr key={p._id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ss-space-2)' }}>
                          <button type="button" className="ss-icon-btn" onClick={() => copyLink(p)} aria-label={`Copy link for ${p.title}`} title="Copy product link">
                            <Copy size={16} aria-hidden="true" />
                          </button>
                          <div className="ss-admin-thumb">
                            {photo && !brokenPhotoIds.has(p._id) ? (
                              <img
                                src={cloudinaryThumb(photo.url, 80)}
                                srcSet={cloudinaryThumbSrcSet(photo.url, 80)}
                                alt=""
                                loading="lazy"
                                onError={() => markPhotoBroken(p._id)}
                              />
                            ) : (
                              <span className="ss-admin-thumb__ph"><Sprout size={20} aria-hidden="true" /></span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <Link to={`/admin/products/${p._id}`} className="ss-admin-table__title">{p.title}</Link>
                      </td>
                      <td className="ss-admin-table__muted">{p.category?.name || '—'}</td>
                      <td style={{ textAlign: 'right' }}>{formatPrice(p.price)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="ss-admin-toggle"
                          onClick={() => toggleActive(p)}
                          title={p.active ? 'Click to deactivate' : 'Click to activate'}
                        >
                          <Badge className={p.active ? 'ss-badge--active' : ''}>{p.active ? 'Active' : 'Inactive'}</Badge>
                        </button>
                      </td>
                      <td>
                        <div className="ss-admin-actions">
                          <button type="button" className="ss-icon-btn" onClick={() => navigate(`/admin/products/${p._id}`)} aria-label={`Edit ${p.title}`}>
                            <Pencil size={16} aria-hidden="true" />
                          </button>
                          <button type="button" className="ss-icon-btn ss-icon-btn--danger" onClick={() => onDelete(p)} aria-label={`Delete ${p.title}`}>
                            <Trash2 size={16} aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </Card>

          <div className="ss-admin-cards" style={{ marginTop: 'var(--ss-space-5)' }}>
            {items.map((p) => {
              const photo = primaryPhoto(p.photos);
              return (
                <div className="ss-admin-card" key={p._id}>
                  <div className="ss-admin-card__row">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ss-space-3)' }}>
                      <button type="button" className="ss-icon-btn" onClick={() => copyLink(p)} aria-label={`Copy link for ${p.title}`} title="Copy product link">
                        <Copy size={16} aria-hidden="true" />
                      </button>
                      <div className="ss-admin-thumb">
                        {photo && !brokenPhotoIds.has(p._id) ? (
                          <img
                            src={cloudinaryThumb(photo.url, 80)}
                            srcSet={cloudinaryThumbSrcSet(photo.url, 80)}
                            alt=""
                            loading="lazy"
                            onError={() => markPhotoBroken(p._id)}
                          />
                        ) : (
                          <span className="ss-admin-thumb__ph"><Sprout size={20} aria-hidden="true" /></span>
                        )}
                      </div>
                      <Link to={`/admin/products/${p._id}`} className="ss-admin-card__title">{p.title}</Link>
                    </div>
                    <button
                      type="button"
                      className="ss-admin-toggle"
                      onClick={() => toggleActive(p)}
                      title={p.active ? 'Click to deactivate' : 'Click to activate'}
                    >
                      <Badge className={p.active ? 'ss-badge--active' : ''}>{p.active ? 'Active' : 'Inactive'}</Badge>
                    </button>
                  </div>
                  <div className="ss-admin-card__row">
                    <span className="ss-admin-card__label">Category</span>
                    <span className="ss-admin-table__muted">{p.category?.name || '—'}</span>
                  </div>
                  <div className="ss-admin-card__row">
                    <span className="ss-admin-card__label">Price</span>
                    <span>{formatPrice(p.price)}</span>
                  </div>
                  <div className="ss-admin-card__row">
                    <div className="ss-admin-actions">
                      <button type="button" className="ss-icon-btn" onClick={() => navigate(`/admin/products/${p._id}`)} aria-label={`Edit ${p.title}`}>
                        <Pencil size={16} aria-hidden="true" />
                      </button>
                      <button type="button" className="ss-icon-btn ss-icon-btn--danger" onClick={() => onDelete(p)} aria-label={`Delete ${p.title}`}>
                        <Trash2 size={16} aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {pages > 1 && (
        <div className="ss-pager" style={{ marginTop: 'var(--ss-space-4)' }}>
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((n) => n - 1)}>Previous</Button>
          <span className="ss-pager__info">Page {page} of {pages} · {total} total</span>
          <Button variant="ghost" size="sm" disabled={page >= pages} onClick={() => setPage((n) => n + 1)}>Next</Button>
        </div>
      )}

      {dialog}
    </div>
  );
}
